import Vue from 'vue'
import { Auth0MfaApi } from 'Api'
import { AUTH0_CLIENT_ID, AUTH0_DOMAIN } from '../utils/constants'
import { uuid } from 'vue-uuid'

const state = {
  authenticators: [],
  enabledAuthenticators: [
    'otp',
    'sms'
  ],
  enrollments: {
    otp: null,
    sms: null
  },
  enrolling: {
    otp: false,
    sms: false
  },
  authenticatorsLoading: false,
  mfaDisableLoading: false,
  otpVerifyLoading: false,
  otpEnrolled: false,
  smsVerifyLoading: false,
  smsEnrolled: false
}

const getters = {
  authenticators: state => {
    return state.authenticators
  },
  enabledAuthenticators: state => {
    return state.enabledAuthenticators
  },
  authenticatorsLoading: state => {
    return state.authenticatorsLoading
  },
  otpEnrolling: state => {
    return state.enrolling.otp
  },
  otpEnrollment: state => {
    return state.enrollments.otp
  },
  otpVerifying: state => {
    return state.otpVerifyLoading
  },
  otpEnrolled: state => {
    return state.otpEnrolled
  },
  smsEnrolling: state => {
    return state.enrolling.sms
  },
  smsEnrollment: state => {
    return state.enrollments.sms
  },
  smsVerifying: state => {
    return state.smsVerifyLoading
  },
  smsEnrolled: state => {
    return state.smsEnrolled
  },
  mfaDisableLoading: state => {
    return state.mfaDisableLoading
  }
}

function get (context, URL, handler, mfaToken) {
  return Auth0MfaApi(mfaToken).get(URL).then(response => handler(context, response))
}

function post (context, URL, data, handler, mfaToken) {
  return Auth0MfaApi(mfaToken).post(URL, data).then(response => handler(context, response))
}

function put (context, data, handler, mfaToken) {
  return Auth0MfaApi(mfaToken).put(URL, data).then(response => handler(context, response))
}

function apiDelete (context, URL, id, handler, mfaToken) {
  return Auth0MfaApi(mfaToken).delete(URL + '/' + id).then(response => handler(context, id, response))
}

function handleAuthenticatorsLoad (context, response) {
  let authenticators = response.data
  authenticators = authenticators.filter((auth) => auth.active)
  state.authenticatorsLoading = false
  if (authenticators.error) {
    context.commit('apiError', authenticators.error)
  } else {
    context.commit('authenticatorsLoaded', authenticators)
  }
}

function handleOtpEnrollmentLoad (context, response) {
  const result = response.data
  state.enrolling.otp = false
  if (result.error) {
    context.commit('apiError', result.error)
  } else {
    context.commit('otpEnrollmentLoaded', result)
  }
  return result
}

function handleOtpVerificationLoad (context, response) {
  const result = response.data
  state.otpVerifyLoading = false
  state.otpEnrolled = true
}

function handleSmsEnrollmentLoad (context, response) {
  const result = response.data
  state.enrolling.sms = false
  if (result.error) {
    context.commit('apiError', result.error)
  } else {
    context.commit('smsEnrollmentLoaded', result)
  }
  return result
}

function handleSmsVerificationLoad (context, response) {
  const result = response.data
  state.smsVerifyLoading = false
  state.smsEnrolled = true
}

function handleDisableAuthenticatorLoad (context, response) {
  state.mfaDisableLoading = false
}

const actions = {
  async getMfaToken (context) {
    const mfaTokenOptions = {
      audience: 'https://' + AUTH0_DOMAIN + '/mfa/',
      scope: 'enroll read:authenticators remove:authenticators'
    }
    try {
      return await Vue.prototype.$auth.getTokenSilently(mfaTokenOptions)
    } catch (err) {
      try {
        return await Vue.prototype.$auth.getTokenWithPopup(mfaTokenOptions)
      } catch (err) {
        context.commit('apiError', err.message)
      }
    }
  },
  async loadAuthenticators (context) {
    if (state.authenticatorsLoading) {
      return
    }
    state.authenticatorsLoading = true
    const mfaToken = await context.dispatch('getMfaToken')
    return get(context, '/mfa/authenticators', handleAuthenticatorsLoad, mfaToken)
  },
  async otpMfaEnroll (context) {
    if (state.enrolling.otp) {
      return
    }
    state.enrolling.otp = true
    const payload = {
      authenticator_types: ['otp'],
      client_id: AUTH0_CLIENT_ID
    }
    const mfaToken = await context.dispatch('getMfaToken')
    return post(context, '/mfa/associate', payload, handleOtpEnrollmentLoad, mfaToken)
  },
  resetEnrollment (context, payload) {
    context.commit('resetEnrollment', payload.type)
  },
  async verifyOtpEnrollment (context, otp) {
    if (state.otpVerifyLoading) {
      return
    }
    state.otpVerifyLoading = true
    const mfaToken = await context.dispatch('getMfaToken')
    const payload = { otp }
    payload.client_id = AUTH0_CLIENT_ID
    payload.grant_type = 'http://auth0.com/oauth/grant-type/mfa-otp'
    payload.mfa_token = mfaToken
    try {
      return await post(context, '/oauth/token', payload, handleOtpVerificationLoad, mfaToken)
    } catch (err) {
      context.commit('apiError', 'Invalid OTP', { root: true })
    }
    state.otpVerifyLoading = false
  },
  async verifySmsEnrollment (context, otp) {
    if (state.smsVerifyLoading) {
      return
    }
    state.smsVerifyLoading = true
    const mfaToken = await context.dispatch('getMfaToken')
    const payload = { }
    payload.client_id = AUTH0_CLIENT_ID
    payload.grant_type = 'http://auth0.com/oauth/grant-type/mfa-oob'
    payload.binding_code = otp
    payload.oob_code = context.getters.smsEnrollment?.oob_code
    payload.mfa_token = mfaToken
    try {
      return await post(context, '/oauth/token', payload, handleSmsVerificationLoad, mfaToken)
    } catch (err) {
      context.commit('apiError', 'Invalid PIN code.', { root: true })
    }
    state.smsVerifyLoading = false
  },
  async disableAuthenticator (context, payload) {
    if (state.mfaDisableLoading) {
      return
    }
    state.mfaDisableLoading = true
    const mfaToken = await context.dispatch('getMfaToken')
    try {
      return await apiDelete(context, '/mfa/authenticators', payload.id, handleDisableAuthenticatorLoad, mfaToken)
    } catch (err) {
      context.commit('apiError', 'Failed to delete authenticator.')
      state.mfaDisableLoading = false
    }
  },
  async smsMfaEnroll (context, { phone_number }) {
    if (state.enrolling.sms) {
      return
    }
    state.enrolling.sms = true
    const payload = {
      authenticator_types: ['oob'],
      oob_channels: ['sms'],
      phone_number: phone_number,
      client_id: AUTH0_CLIENT_ID
    }
    const mfaToken = await context.dispatch('getMfaToken')
    return await post(context, '/mfa/associate', payload, handleSmsEnrollmentLoad, mfaToken)
  }
}

const mutations = {
  authenticatorsLoaded (state, authenticators) {
    state.authenticators = authenticators
  },
  otpEnrollmentLoaded (state, result) {
    state.enrollments.otp = result
  },
  smsEnrollmentLoaded (state, result) {
    state.enrollments.sms = result
  },
  resetEnrollment (state, type) {
    state.enrolling[type] = false
    state.enrollments[type] = null
  }
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
  name: 'mfaStore'
}
